import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Portfolio from './components/Portfolio';
import ServiceAreas from './components/ServiceAreas';
import ProjectForm from './components/ProjectForm';
import Testimonials from './components/Testimonials';
import CTA from './components/CTA';
import Footer from './components/Footer';
import Navbar from './components/Navbar';

export default function HomePage() {
  const [activeFilter, setActiveFilter] = useState('All');

  useEffect(() => {
    // Add Schema.org JSON-LD for homepage
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.bellportbranding.com';
    
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: 'Bellport Branding',
      url: siteUrl,
      logo: `${siteUrl}/logo.png`,
      description: 'Long Island digital agency specializing in website design, video production, social media management, and cybersecurity for small businesses in Nassau and Suffolk County.',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Bellport',
        addressRegion: 'NY',
        addressCountry: 'US'
      },
      telephone: '+1-631-640-7888',
      sameAs: [
        'https://www.facebook.com/bellportbranding',
        'https://www.instagram.com/bellportbranding',
        'https://www.linkedin.com/company/bellportbranding'
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+1-631-640-7888',
        contactType: 'Customer Service',
        areaServed: 'Long Island, NY',
        availableLanguage: 'English'
      }
    };

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#localbusiness`,
      name: 'Bellport Branding',
      image: `${siteUrl}/logo.png`,
      url: siteUrl,
      telephone: '+1-631-640-7888',
      description: 'Bellport Branding is a Long Island digital agency helping small businesses grow with professional website design, video production, social media management, and cybersecurity services. Serving Nassau and Suffolk County.',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Bellport',
        addressRegion: 'NY',
        addressCountry: 'US'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 40.7891,
        longitude: -73.1350
      },
      areaServed: [
        { '@type': 'City', name: 'Huntington, NY' },
        { '@type': 'City', name: 'Babylon, NY' },
        { '@type': 'City', name: 'Islip, NY' },
        { '@type': 'City', name: 'Smithtown, NY' },
        { '@type': 'City', name: 'Brookhaven, NY' },
        { '@type': 'City', name: 'Patchogue, NY' }
      ],
      priceRange: '$$',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00'
        }
      ],
      sameAs: [
        'https://www.facebook.com/bellportbranding',
        'https://www.instagram.com/bellportbranding',
        'https://www.linkedin.com/company/bellportbranding'
      ]
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/#webpage`,
      name: 'Long Island Website Design & Video Production | Small Business Digital Agency',
      description: 'Bellport Branding helps Long Island small businesses grow with professional website design, video production, social media management & cybersecurity. Serving Nassau & Suffolk County.',
      url: siteUrl,
      inLanguage: 'en-US',
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        name: 'Bellport Branding',
        url: siteUrl
      }
    };

    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${siteUrl}/#service`,
      serviceType: 'Digital Marketing and Web Design',
      provider: {
        '@id': `${siteUrl}/#organization`
      },
      areaServed: [
        { '@type': 'County', name: 'Nassau County, NY' },
        { '@type': 'County', name: 'Suffolk County, NY' }
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Long Island Digital Services',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Website Design Long Island',
              description: 'Professional website design for Long Island small businesses in Nassau and Suffolk County.'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Video Production Long Island',
              description: 'Professional videography and video production for Long Island businesses.'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Social Media Management',
              description: 'Strategic social media management for Long Island small businesses.'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Cybersecurity Services',
              description: 'Ethical hacking and cybersecurity protection for Long Island businesses.'
            }
          }
        ]
      }
    };

    const addSchema = (schema: object, id: string) => {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    };

    addSchema(organizationSchema, 'organization-schema');
    addSchema(localBusinessSchema, 'localbusiness-schema');
    addSchema(webPageSchema, 'webpage-schema');
    addSchema(serviceSchema, 'service-schema');

    return () => {
      // Cleanup schemas on unmount
      ['organization-schema', 'localbusiness-schema', 'webpage-schema', 'service-schema'].forEach(id => {
        const script = document.getElementById(id);
        if (script) script.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Hero />
      <About />
      <Services />
      <Portfolio activeFilter={activeFilter} setActiveFilter={setActiveFilter} />
      <ServiceAreas />
      <Testimonials />
      <ProjectForm />
      <CTA />
      <Footer />
    </div>
  );
}
